import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/stats');
      setStats(res.data.data);
    } catch (err) {
      // Use mock data if API fails
      setStats({
        totalUsers: 5234,
        totalParkings: 12,
        totalBookings: 1847,
        totalRevenue: 284650,
        activeBookings: 43,
        todayBookings: 127,
        todayRevenue: 18450,
        revenueByDay: [
          { day: 'Mon', revenue: 12400 },
          { day: 'Tue', revenue: 18200 },
          { day: 'Wed', revenue: 15800 },
          { day: 'Thu', revenue: 22100 },
          { day: 'Fri', revenue: 28400 },
          { day: 'Sat', revenue: 31200 },
          { day: 'Sun', revenue: 19800 },
        ],
        bookingsByStatus: [
          { name: 'Completed', value: 1420 },
          { name: 'Active', value: 43 },
          { name: 'Cancelled', value: 384 },
        ],
        topParkings: [
          { name: 'Gulmandi Parking', bookings: 342, revenue: 48600 },
          { name: 'Nirala Bazaar', bookings: 287, revenue: 41200 },
          { name: 'Prozone Mall', bookings: 256, revenue: 38400 },
          { name: 'CIDCO N-6', bookings: 198, revenue: 28200 },
          { name: 'Aurangpura', bookings: 176, revenue: 24800 },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '64px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers?.toLocaleString(), icon: '👥', color: '#6366f1', change: '+12%' },
    { label: 'Total Parkings', value: stats?.totalParkings, icon: '🅿️', color: '#8b5cf6', change: '+2' },
    { label: 'Total Bookings', value: stats?.totalBookings?.toLocaleString(), icon: '📋', color: '#10b981', change: '+8%' },
    { label: 'Total Revenue', value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`, icon: '💰', color: '#f59e0b', change: '+15%' },
    { label: 'Active Now', value: stats?.activeBookings, icon: '🚗', color: '#10b981', change: 'Live' },
    { label: "Today's Bookings", value: stats?.todayBookings, icon: '📅', color: '#6366f1', change: 'Today' },
    { label: "Today's Revenue", value: `₹${(stats?.todayRevenue || 0).toLocaleString()}`, icon: '💵', color: '#f59e0b', change: 'Today' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div style={{ background: '#1e1e3a', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px', padding: '12px' }}>
          <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>{label}</p>
          <p style={{ color: '#6366f1', fontWeight: '700' }}>₹{payload[0].value?.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '900', marginBottom: '6px' }}>Admin Dashboard</h1>
            <p style={{ color: '#64748b' }}>SmartPark CSN — Overview</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link to="/admin/parkings" style={{
              background: 'rgba(99,102,241,0.1)', color: '#6366f1',
              textDecoration: 'none', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.3)',
              padding: '10px 18px', fontSize: '13px', fontWeight: '600',
            }}>🅿️ Manage Parkings</Link>
            <Link to="/admin/users" style={{
              background: 'rgba(139,92,246,0.1)', color: '#8b5cf6',
              textDecoration: 'none', borderRadius: '10px', border: '1px solid rgba(139,92,246,0.3)',
              padding: '10px 18px', fontSize: '13px', fontWeight: '600',
            }}>👥 Manage Users</Link>
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {statCards.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px', padding: '20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>{s.icon}</span>
                <span style={{ fontSize: '11px', color: '#10b981', background: 'rgba(16,185,129,0.1)', borderRadius: '6px', padding: '2px 6px', fontWeight: '600' }}>
                  {s.change}
                </span>
              </div>
              <div style={{ fontSize: '22px', fontWeight: '900', color: s.color, marginBottom: '4px' }}>{s.value}</div>
              <div style={{ color: '#64748b', fontSize: '12px' }}>{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Revenue Chart */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}>Weekly Revenue</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.revenueByDay || []}>
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Status Pie */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}>Booking Status</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={stats?.bookingsByStatus || []} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                  {(stats?.bookingsByStatus || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ background: '#1e1e3a', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
              {(stats?.bookingsByStatus || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>{item.name}</span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: '700' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Parkings Table */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700' }}>Top Performing Parkings</h3>
            <Link to="/admin/parkings" style={{ color: '#6366f1', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>View All →</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Parking Name', 'Bookings', 'Revenue', 'Performance'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: '#64748b', fontSize: '12px', fontWeight: '600', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(stats?.topParkings || []).map((p, i) => {
                  const maxRev = Math.max(...(stats?.topParkings || []).map(x => x.revenue));
                  const pct = (p.revenue / maxRev) * 100;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '14px 12px', fontWeight: '600', fontSize: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#6366f1', fontWeight: '800', fontSize: '12px' }}>#{i + 1}</span>
                          {p.name}
                        </div>
                      </td>
                      <td style={{ padding: '14px 12px', color: '#94a3b8', fontSize: '14px' }}>{p.bookings}</td>
                      <td style={{ padding: '14px 12px', fontWeight: '700', color: '#10b981', fontSize: '14px' }}>₹{p.revenue.toLocaleString()}</td>
                      <td style={{ padding: '14px 12px', minWidth: '120px' }}>
                        <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: '3px' }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}